import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { locationsRes, safe } from "../../lib/locations";
import { toast } from "sonner";
import {
  Plus, Trash, Calendar, X, PencilSimple, MapPin, Info,
} from "@phosphor-icons/react";

/**
 * Master Control → Special Collection Dates
 * Ad-hoc / seasonal collection drives scoped by any-of District / LSGD /
 * Health-Zone with a discrete-date list OR a start→end range.
 */

const EMPTY_FORM = {
  id: null,
  title: "",
  notes: "",
  district_ids: [],
  lsgd_ids: [],
  zone_ids: [],
  special_zone_ids: [],
  all_districts: false,
  all_lsgds: false,
  all_zones: false,
  dates: [],
  range_start: "",
  range_end: "",
  is_active: true,
};

const EMPTY_SZ = { id: null, name: "", notes: "", zone_ids: [] };

export default function SpecialCollectionDates() {
  const [items, setItems] = useState([]);
  const [zones, setZones] = useState([]);
  const [locations, setLocations] = useState([]);
  const [specialZones, setSpecialZones] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [drawer, setDrawer] = useState(false);
  const [dateInput, setDateInput] = useState("");
  const [busy, setBusy] = useState(false);
  // Special-Health-Zone side-panel state
  const [szForm, setSzForm] = useState(EMPTY_SZ);
  const [szDrawer, setSzDrawer] = useState(false);
  const [szBusy, setSzBusy] = useState(false);

  const load = async () => {
    try {
      const [s, z, l, sz] = await Promise.all([
        safe(api.get("/special-collection-dates")),
        safe(api.get("/zones")),
        locationsRes(),
        safe(api.get("/special-health-zones")),
      ]);
      setItems(s.data); setZones(z.data); setLocations(l.data); setSpecialZones(sz.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const districts = useMemo(
    () => locations.filter(x => x.type === "district"), [locations]);
  const lsgds = useMemo(() => {
    if (form.all_districts) return locations.filter(x => x.type === "lsgd");
    if (form.district_ids.length === 0) return locations.filter(x => x.type === "lsgd");
    return locations.filter(x => x.type === "lsgd" && form.district_ids.includes(x.parent_id));
  }, [locations, form.district_ids, form.all_districts]);
  const filteredZones = useMemo(() => {
    if (form.all_lsgds || form.lsgd_ids.length === 0) {
      if (form.all_districts || form.district_ids.length === 0) return zones;
      return zones.filter(z => form.district_ids.includes(z.district_id));
    }
    return zones.filter(z => form.lsgd_ids.includes(z.lsgd_id));
  }, [zones, form.lsgd_ids, form.district_ids, form.all_lsgds, form.all_districts]);

  const openNew = () => { setForm(EMPTY_FORM); setDateInput(""); setDrawer(true); };
  const openEdit = (row) => {
    setForm({
      id: row.id,
      title: row.title || "",
      notes: row.notes || "",
      district_ids: row.district_ids || [],
      lsgd_ids: row.lsgd_ids || [],
      zone_ids: row.zone_ids || [],
      special_zone_ids: row.special_zone_ids || [],
      all_districts: !!row.all_districts,
      all_lsgds: !!row.all_lsgds,
      all_zones: !!row.all_zones,
      dates: row.dates || [],
      range_start: "",
      range_end: "",
      is_active: row.is_active !== false,
    });
    setDateInput("");
    setDrawer(true);
  };
  const close = () => setDrawer(false);

  // ------- Special Health Zone drawer handlers -------
  const openNewSz = () => { setSzForm(EMPTY_SZ); setSzDrawer(true); };
  const openEditSz = (row) => {
    setSzForm({ id: row.id, name: row.name || "", notes: row.notes || "",
                 zone_ids: row.zone_ids || [] });
    setSzDrawer(true);
  };
  const closeSz = () => setSzDrawer(false);
  const toggleSzZone = (id) => {
    setSzForm(f => {
      const cur = new Set(f.zone_ids);
      if (cur.has(id)) cur.delete(id); else cur.add(id);
      return { ...f, zone_ids: Array.from(cur) };
    });
  };
  const submitSz = async () => {
    if (!szForm.name.trim()) { toast.error("Group name is required"); return; }
    if (szForm.zone_ids.length === 0) { toast.error("Pick at least one health zone"); return; }
    setSzBusy(true);
    try {
      const payload = { name: szForm.name, notes: szForm.notes, zone_ids: szForm.zone_ids };
      if (szForm.id) {
        await api.put(`/special-health-zones/${szForm.id}`, payload);
        toast.success("Group updated");
      } else {
        await api.post("/special-health-zones", payload);
        toast.success("Group created");
      }
      closeSz(); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSzBusy(false);
  };
  const deleteSz = async (row) => {
    if (!window.confirm(`Delete special zone "${row.name}"?`)) return;
    try {
      await api.delete(`/special-health-zones/${row.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleId = (field, id) => {
    setForm(f => {
      const cur = new Set(f[field]);
      if (cur.has(id)) cur.delete(id); else cur.add(id);
      return { ...f, [field]: Array.from(cur) };
    });
  };
  const addDate = () => {
    const d = (dateInput || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) { toast.error("Pick a valid date"); return; }
    if (form.dates.includes(d)) { toast("Already added"); return; }
    setForm(f => ({ ...f, dates: [...f.dates, d].sort() }));
    setDateInput("");
  };
  const removeDate = (d) =>
    setForm(f => ({ ...f, dates: f.dates.filter(x => x !== d) }));

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const anyTarget = form.all_districts || form.all_lsgds || form.all_zones
      || form.district_ids.length || form.lsgd_ids.length || form.zone_ids.length
      || form.special_zone_ids.length;
    if (!anyTarget) { toast.error("Pick at least one district / LSGD / zone / special-zone"); return; }
    if (form.dates.length === 0 && !(form.range_start && form.range_end)) {
      toast.error("Add at least one date (or a range)");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      delete payload.id;
      if (form.id) {
        await api.put(`/special-collection-dates/${form.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/special-collection-dates", payload);
        toast.success("Created");
      }
      close();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const del = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await api.delete(`/special-collection-dates/${row.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const districtName = (id) => districts.find(d => d.id === id)?.name || id;
  const lsgdName = (id) => locations.find(l => l.id === id)?.name || id;
  const zoneName = (id) => zones.find(z => z.id === id)?.name || id;
  const szName = (id) => specialZones.find(s => s.id === id)?.name || id;

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#8A8780]">Master Control</div>
            <h1 className="text-3xl font-heading text-[#1A1A1A] mt-1">Special Collection Dates</h1>
            <p className="text-sm text-[#5C5A55] mt-1 max-w-2xl">
              Schedule one-off collection drives (monthly, annual, seasonal) for
              specific districts, LSGDs or health zones — customers and staff will
              see the upcoming date on their dashboards.
            </p>
          </div>
          <button data-testid="new-special-date-btn"
                  onClick={openNew}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#2D5A27] text-white text-sm font-medium hover:bg-[#3A7033]">
            <Plus weight="bold" /> New Special Date
          </button>
        </div>

        {/* Special Health Zones — named zone groups reusable in the picker */}
        <div className="border border-[#E5E2D9] rounded-lg bg-white p-4" data-testid="sz-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#5C5A55] font-semibold">Special Health Zones</div>
              <div className="text-xs text-[#8A8780] mt-0.5 max-w-xl">
                Reusable named groups of health zones (e.g. "North Belt", "Coastal Ward Cluster")
                so you can target multiple zones in one click when creating a special date.
              </div>
            </div>
            <button data-testid="new-sz-btn" onClick={openNewSz}
                    className="inline-flex items-center gap-1 h-8 px-3 border border-[#2D5A27] text-[#2D5A27] rounded-md text-xs hover:bg-[#EDF7EA]">
              <Plus weight="bold" size={12} /> New Group
            </button>
          </div>
          {specialZones.length === 0 ? (
            <div className="mt-3 text-[11px] text-[#8A8780]">No special health zones yet.</div>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {specialZones.map(sz => (
                <div key={sz.id} data-testid={`sz-row-${sz.id}`}
                     className="inline-flex items-center gap-1 bg-[#EDF7EA] border border-[#2D5A27]/25 text-[#1A3317] px-2 py-1 rounded text-xs">
                  <span className="font-medium">{sz.name}</span>
                  <span className="text-[10px] text-[#5C5A55]">· {sz.zone_ids?.length || 0} zones</span>
                  <button onClick={() => openEditSz(sz)} className="ml-1 text-[#2D5A27] hover:opacity-70">
                    <PencilSimple size={12} />
                  </button>
                  <button onClick={() => deleteSz(sz)} className="text-[#8A2C2C] hover:opacity-70">
                    <Trash size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid gap-3">
          {items.length === 0 && (
            <div className="border border-dashed border-[#E5E2D9] rounded-lg p-10 text-center text-[#8A8780]">
              No special collection dates yet. Click <b>New Special Date</b> to plan one.
            </div>
          )}
          {items.map(r => {
            const upcoming = (r.dates || []).filter(d => d >= new Date().toISOString().slice(0,10));
            const past = (r.dates || []).length - upcoming.length;
            return (
              <div key={r.id} data-testid={`special-row-${r.id}`}
                   className="border border-[#E5E2D9] rounded-lg bg-white p-4 hover:border-[#2D5A27]/40 transition">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Calendar size={18} className="text-[#2D5A27]" />
                      <h3 className="font-semibold text-[#1A1A1A] truncate">{r.title}</h3>
                      {r.is_active === false && (
                        <span className="text-[10px] uppercase tracking-[0.15em] bg-[#F0EFEA] text-[#8A8780] px-2 py-0.5 rounded">Inactive</span>
                      )}
                    </div>
                    {r.notes && <div className="text-sm text-[#5C5A55] mt-1 whitespace-pre-wrap">{r.notes}</div>}
                    <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                      {r.all_districts ? <Chip>All Districts</Chip> :
                        (r.district_ids || []).map(id => <Chip key={id}><MapPin size={10} className="inline mr-0.5" />{districtName(id)}</Chip>)}
                      {r.all_lsgds ? <Chip>All LSGDs</Chip> :
                        (r.lsgd_ids || []).map(id => <Chip key={id}>{lsgdName(id)}</Chip>)}
                      {r.all_zones ? <Chip color="green">All Zones</Chip> :
                        (r.zone_ids || []).map(id => <Chip key={id} color="green">{zoneName(id)}</Chip>)}
                      {(r.special_zone_ids || []).map(id => (
                        <Chip key={id} color="purple">◆ {szName(id)}</Chip>
                      ))}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {upcoming.slice(0, 12).map(d => (
                        <span key={d} className="text-[11px] font-mono bg-[#EDF7EA] border border-[#2D5A27]/30 text-[#1A3317] px-2 py-1 rounded">
                          {formatDate(d)}
                        </span>
                      ))}
                      {upcoming.length > 12 && (
                        <span className="text-[11px] text-[#8A8780] self-center">+{upcoming.length - 12} more</span>
                      )}
                      {upcoming.length === 0 && past > 0 && (
                        <span className="text-[11px] text-[#8A8780]">All {past} dates have passed</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`edit-${r.id}`} onClick={() => openEdit(r)}
                            className="h-9 px-3 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2] inline-flex items-center gap-1">
                      <PencilSimple size={14} /> Edit
                    </button>
                    <button data-testid={`del-${r.id}`} onClick={() => del(r)}
                            className="h-9 w-9 border border-[#E5E2D9] rounded-md text-[#8A2C2C] hover:bg-[#FCE9E9] inline-flex items-center justify-center">
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {drawer && (
          <Drawer onClose={close} title={form.id ? "Edit Special Date" : "New Special Date"}>
            <div className="space-y-5">
              <FieldRow label="Title *">
                <input data-testid="scd-title" value={form.title}
                       onChange={e => setForm(f => ({...f, title: e.target.value}))}
                       maxLength={140}
                       placeholder="Onam Sanitation Drive, Annual Bio-medical Sweep…"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white" />
              </FieldRow>

              <FieldRow label="Notes / instructions for staff">
                <textarea data-testid="scd-notes" value={form.notes}
                          onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                          rows={2} maxLength={1000}
                          className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md bg-white" />
              </FieldRow>

              <SectionHeading icon={<MapPin size={14} />} text="Target zones — any match" />

              <MultiPicker
                label="Districts"
                testid="scd-districts"
                items={districts}
                selectedIds={form.district_ids}
                allSelected={form.all_districts}
                onToggle={(id) => toggleId("district_ids", id)}
                onSelectAll={(v) => setForm(f => ({
                  ...f, all_districts: v, district_ids: v ? [] : f.district_ids,
                }))}
              />
              <MultiPicker
                label="LSGDs"
                testid="scd-lsgds"
                items={lsgds}
                selectedIds={form.lsgd_ids}
                allSelected={form.all_lsgds}
                onToggle={(id) => toggleId("lsgd_ids", id)}
                onSelectAll={(v) => setForm(f => ({
                  ...f, all_lsgds: v, lsgd_ids: v ? [] : f.lsgd_ids,
                }))}
              />
              <MultiPicker
                label="Health Zones"
                testid="scd-zones"
                items={filteredZones}
                selectedIds={form.zone_ids}
                allSelected={form.all_zones}
                onToggle={(id) => toggleId("zone_ids", id)}
                onSelectAll={(v) => setForm(f => ({
                  ...f, all_zones: v, zone_ids: v ? [] : f.zone_ids,
                }))}
              />

              {/* Special Health Zone picker — named zone groups. Selecting
                  a group expands into every zone inside it at match time. */}
              {specialZones.length > 0 && !form.all_zones && (
                <div>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-[#5C5A55] mb-1.5">
                    Special Health Zones ({specialZones.length})
                  </div>
                  <div data-testid="scd-special-zones" className="max-h-40 overflow-y-auto border border-[#3B2C7F]/25 rounded-md p-2 bg-[#FAF9FE]">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                      {specialZones.map(sz => (
                        <label key={sz.id}
                                data-testid={`scd-sz-${sz.id}`}
                                className="inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-[#EDE8F7] cursor-pointer">
                          <input type="checkbox"
                                  checked={form.special_zone_ids.includes(sz.id)}
                                  onChange={() => toggleId("special_zone_ids", sz.id)} />
                          <span className="text-xs text-[#1A1A1A] truncate">
                            <span className="text-[#3B2C7F]">◆</span> {sz.name}
                            <span className="text-[10px] text-[#8A8780] ml-1">·{sz.zone_ids?.length || 0}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <SectionHeading icon={<Calendar size={14} />} text="Collection dates" />

              <div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mb-1.5">Individual dates</div>
                <div className="flex items-center gap-2">
                  <input data-testid="scd-date-input" type="date" value={dateInput}
                         min={new Date().toISOString().slice(0,10)}
                         onChange={e => setDateInput(e.target.value)}
                         className="h-10 px-3 border border-[#E5E2D9] rounded-md bg-white" />
                  <button data-testid="scd-date-add" type="button" onClick={addDate}
                          className="h-10 px-4 border border-[#2D5A27] text-[#2D5A27] rounded-md text-sm hover:bg-[#EDF7EA] inline-flex items-center gap-1">
                    <Plus size={14} weight="bold" /> Add
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="scd-date-chips">
                  {form.dates.map(d => (
                    <span key={d} className="inline-flex items-center gap-1 bg-[#EDF7EA] border border-[#2D5A27]/30 text-[#1A3317] px-2 py-1 rounded text-[11px] font-mono">
                      {formatDate(d)}
                      <button type="button" onClick={() => removeDate(d)}
                              className="ml-1 text-[#8A2C2C] hover:opacity-80"><X size={11} weight="bold" /></button>
                    </span>
                  ))}
                  {form.dates.length === 0 && (
                    <span className="text-xs text-[#8A8780]">No individual dates yet</span>
                  )}
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-[#8A8780] mb-1.5">Or a date range (fills every day in between)</div>
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid="scd-range-start" type="date" value={form.range_start}
                         min={new Date().toISOString().slice(0,10)}
                         onChange={e => setForm(f => ({...f, range_start: e.target.value}))}
                         className="h-10 px-3 border border-[#E5E2D9] rounded-md bg-white" />
                  <input data-testid="scd-range-end" type="date" value={form.range_end}
                         min={form.range_start || new Date().toISOString().slice(0,10)}
                         onChange={e => setForm(f => ({...f, range_end: e.target.value}))}
                         className="h-10 px-3 border border-[#E5E2D9] rounded-md bg-white" />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input id="is_active" type="checkbox" checked={form.is_active}
                       onChange={e => setForm(f => ({...f, is_active: e.target.checked}))} />
                <label htmlFor="is_active" className="text-sm text-[#1A1A1A]">Active — surface on customer &amp; staff apps</label>
              </div>
            </div>

            <div className="mt-6 flex gap-2 justify-end pt-4 border-t border-[#E5E2D9]">
              <button onClick={close}
                      className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
                Cancel
              </button>
              <button data-testid="scd-save" disabled={busy} onClick={submit}
                      className="h-10 px-5 bg-[#2D5A27] text-white rounded-md text-sm font-medium hover:bg-[#3A7033] disabled:opacity-50">
                {busy ? "Saving…" : (form.id ? "Save changes" : "Create")}
              </button>
            </div>
          </Drawer>
        )}

        {szDrawer && (
          <Drawer onClose={closeSz} title={szForm.id ? "Edit Special Health Zone" : "New Special Health Zone"}>
            <div className="space-y-4">
              <FieldRow label="Group name *">
                <input data-testid="sz-name" value={szForm.name}
                       onChange={e => setSzForm(f => ({...f, name: e.target.value}))}
                       maxLength={140}
                       placeholder="North Belt · Coastal Cluster · Special Ward Group…"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white" />
              </FieldRow>
              <FieldRow label="Notes (optional)">
                <textarea data-testid="sz-notes" value={szForm.notes}
                          onChange={e => setSzForm(f => ({...f, notes: e.target.value}))}
                          rows={2} maxLength={1000}
                          className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md bg-white" />
              </FieldRow>
              <div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-[#5C5A55] mb-1.5">
                  Health Zones ({zones.length})
                </div>
                <div data-testid="sz-zones" className="max-h-56 overflow-y-auto border border-[#E5E2D9] rounded-md p-2 bg-[#FAFAF7]">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                    {zones.length === 0 && (
                      <div className="col-span-full text-[11px] text-[#8A8780] py-2 px-1">No health zones exist yet</div>
                    )}
                    {zones.map(z => (
                      <label key={z.id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-[#EDF7EA] cursor-pointer">
                        <input type="checkbox" checked={szForm.zone_ids.includes(z.id)}
                               onChange={() => toggleSzZone(z.id)} />
                        <span className="text-xs text-[#1A1A1A] truncate">{z.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="text-[10px] text-[#8A8780] mt-1">
                  {szForm.zone_ids.length} zone{szForm.zone_ids.length === 1 ? "" : "s"} selected
                </div>
              </div>
            </div>
            <div className="mt-6 flex gap-2 justify-end pt-4 border-t border-[#E5E2D9]">
              <button onClick={closeSz}
                      className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
                Cancel
              </button>
              <button data-testid="sz-save" disabled={szBusy} onClick={submitSz}
                      className="h-10 px-5 bg-[#2D5A27] text-white rounded-md text-sm font-medium hover:bg-[#3A7033] disabled:opacity-50">
                {szBusy ? "Saving…" : (szForm.id ? "Save changes" : "Create group")}
              </button>
            </div>
          </Drawer>
        )}
      </div>
    </AppShell>
  );
}

// ----------------------------- small helpers -----------------------------
const Chip = ({ color, children }) => {
  const cls = color === "green"
    ? "bg-[#EDF7EA] text-[#1A3317] border-[#2D5A27]/25"
    : color === "purple"
    ? "bg-[#F0EDF7] text-[#3B2C7F] border-[#3B2C7F]/25"
    : "bg-[#F7F6F2] text-[#5C5A55] border-[#E5E2D9]";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 border rounded text-[10px] uppercase tracking-[0.1em] ${cls}`}>
      {children}
    </span>
  );
};

const SectionHeading = ({ icon, text }) => (
  <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#5C5A55] mt-3 pt-3 border-t border-[#E5E2D9]/60">
    {icon} {text}
  </div>
);

const FieldRow = ({ label, children }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.15em] text-[#5C5A55] mb-1.5">{label}</label>
    {children}
  </div>
);

const MultiPicker = ({ label, testid, items, selectedIds, allSelected, onToggle, onSelectAll }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <div className="text-[11px] uppercase tracking-[0.15em] text-[#5C5A55]">{label} ({items.length})</div>
      <label className="text-[11px] flex items-center gap-1 text-[#2D5A27] cursor-pointer">
        <input data-testid={`${testid}-all`} type="checkbox" checked={allSelected}
               onChange={e => onSelectAll(e.target.checked)} />
        Select all
      </label>
    </div>
    {!allSelected && (
      <div data-testid={testid} className="max-h-40 overflow-y-auto border border-[#E5E2D9] rounded-md p-2 bg-[#FAFAF7]">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
          {items.length === 0 && <div className="text-[11px] text-[#8A8780] col-span-full py-2 px-1">No {label.toLowerCase()} available for the current selection</div>}
          {items.map(x => (
            <label key={x.id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-[#EDF7EA] cursor-pointer">
              <input type="checkbox" checked={selectedIds.includes(x.id)}
                     onChange={() => onToggle(x.id)} />
              <span className="text-xs text-[#1A1A1A] truncate">{x.name}</span>
            </label>
          ))}
        </div>
      </div>
    )}
    {allSelected && (
      <div className="text-[11px] px-3 py-2 rounded border border-[#2D5A27]/30 bg-[#EDF7EA] text-[#1A3317] flex items-center gap-1.5">
        <Info size={12} weight="bold" /> Every {label.toLowerCase()} in the tenant will be targeted.
      </div>
    )}
  </div>
);

const Drawer = ({ children, onClose, title }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4">
    <div className="bg-[#FAFAF7] rounded-lg shadow-xl w-full max-w-2xl mt-8 mb-8">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E2D9]">
        <h3 className="font-heading text-lg text-[#1A1A1A]">{title}</h3>
        <button onClick={onClose} className="text-[#8A8780] hover:text-[#1A1A1A]"><X size={20} /></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    const day = d.getDate().toString().padStart(2, "0");
    const mon = d.toLocaleString("en-US", { month: "short" });
    const wd = d.toLocaleString("en-US", { weekday: "short" });
    return `${wd} · ${day} ${mon}`;
  } catch { return iso; }
}
